import { useEffect, useMemo, useState } from 'react'
import { useHistory, useParams } from 'react-router-dom'

import { Breadcrumb, Button, Icon } from 'antd'
import { useFormikContext } from 'formik'

import { ConfirmLeaveWithoutSavingModal } from '../../../../../../components/ConfirmLeaveWithoutSavingModal'
import { RULES, validRule } from '../../../../../../constants/Entitlements'
import { getMessage } from '../../../../../../util/IntlMessages'
import { isAdmin } from '../../../../../../util/profiles'
import { actionStatus, actionStatusMap } from '../../constants'
import { useCreateActionContext } from '../../contexts/action'
import './styles.less'

export function ActionHeader() {
  const params = useParams()
  const history = useHistory()

  const { isNewAction, isPreview, setIsPreview, isReadOnly, isSubmitting, beginSubmit, endSubmit } =
    useCreateActionContext()

  const { submitForm, setValues, values, dirty, validateForm } = useFormikContext()

  const currentSnapshot = useMemo(() => JSON.stringify(values), [values])
  const [savedSnapshot, setSavedSnapshot] = useState(currentSnapshot)

  useEffect(() => {
    if (!dirty) {
      setSavedSnapshot(currentSnapshot)
    }
  }, [dirty, currentSnapshot])

  const hasChanges = !isNewAction && dirty && savedSnapshot !== currentSnapshot

  async function confirmAction(action) {
    /*
      [AD-14811] O lock precisa ser adquirido AQUI, de forma sincrona no clique. Antes ele so era adquirido
      dentro do ActionForm, depois de `validateForm` + `submitForm` -- duas passadas assincronas de validacao
      yup sobre o schema montado a partir de todos os custom fields. Nesse intervalo o botao seguia habilitado
      e sem feedback, que e exatamente o que faz o usuario clicar de novo.
    */
    if (!beginSubmit()) return false

    try {
      const isActionFinished = action === 'finish'

      let nextValues = values

      if (isActionFinished) {
        nextValues = {
          ...values,
          baseFields: {
            ...values.baseFields,
            finish: true,
            actionStatusId: actionStatusMap.get(actionStatus.DONE)
          }
        }

        await setValues(nextValues)
      }

      const errors = await validateForm(nextValues)
      const hasErrors = Object.keys(errors || {}).length > 0

      await submitForm()

      if (!hasErrors) {
        setSavedSnapshot(JSON.stringify(nextValues))
        return true
      }

      return false
    } finally {
      endSubmit()
    }
  }

  const hasAccess = validRule(RULES.ACTION)
  const sysAdmin = isAdmin()

  return (
    <div className="actionHeader">
      <ConfirmLeaveWithoutSavingModal
        when={hasChanges}
        leaveRoute="/actions/list"
        saveAction={() => confirmAction('save')}
        isSubmitting={isSubmitting}
      />

      <Breadcrumb>
        <Breadcrumb.Item onClick={() => history.push('/')}>
          <Icon
            className="actionHeader__icon actionHeader__homeIcon"
            type="home"
            theme="filled"
            onClick={() => history.push('/')}
          />
        </Breadcrumb.Item>

        <Breadcrumb.Item className="actionHeader__breadcrumbItem" onClick={() => history.push('/actions/list')}>
          {getMessage('actions')}
        </Breadcrumb.Item>

        <Breadcrumb.Item className="actionHeader__breadcrumbItem">
          {`${params.id ? getMessage('updateAction') : getMessage('registerAction')}`}
        </Breadcrumb.Item>
      </Breadcrumb>

      <div className="actionHeader__buttonsWrapper">
        {isPreview ? (
          <>
            <Button className="actionHeader__btnReturn" onClick={() => setIsPreview(false)}>
              <Icon type="left" theme="outlined" className="actionHeader__icon" />
              {getMessage('reEdit')}
            </Button>

            <Button
              className="actionHeader__btnAction"
              type="primary"
              loading={isSubmitting}
              disabled={isSubmitting}
              onClick={() => confirmAction('finish')}
            >
              {getMessage('finish')}
            </Button>
          </>
        ) : (
          <>
            <Button className="actionHeader__btnReturn" id="back" onClick={() => history.push('/actions/list')}>
              <Icon type="left" theme="outlined" className="actionHeader__backIcon" />
              {getMessage('back')}
            </Button>

            {(sysAdmin || (!isReadOnly && hasAccess)) && (
              <>
                <Button
                  className="actionHeader__btnAction"
                  id="save"
                  type="primary"
                  loading={isSubmitting}
                  disabled={isSubmitting}
                  onClick={() => confirmAction('save')}
                >
                  {isNewAction ? getMessage('register') : getMessage('update')}
                </Button>

                <Button
                  className="actionHeader__btnAction"
                  id="finish"
                  type="primary"
                  loading={isSubmitting}
                  disabled={isSubmitting}
                  onClick={() => confirmAction('finish')}
                >
                  {getMessage('finishAction')}
                </Button>
              </>
            )}
          </>
        )}
      </div>
    </div>
  )
}
